import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { getTrafficFlowTileUrl, getTrafficIncidentTileUrl, getTomTomBaseTileUrl } from '../services/tomtomService';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Contact, Alert, LatLng, SafetyStatus, HazardType, NewsItem } from '../types';
import { renderToStaticMarkup } from 'react-dom/server';

// --- ICONS ---
import { Car, CloudLightning, Info } from 'lucide-react'; // RESTORED for bottom code
import { GiEarthCrack, GiBigWave, GiWildfires, GiTornado, GiNuclearPlant, GiRiotShield, GiPoliceBadge, GiFallingRocks, GiHealthNormal } from 'react-icons/gi';
import { FaBiohazard, FaCarCrash, FaRadiation, FaTemperatureHigh } from 'react-icons/fa';
import { MdSevereCold, MdThunderstorm } from 'react-icons/md';
import { RiTrafficLightFill } from 'react-icons/ri';
import { IoMdWarning } from 'react-icons/io';

import RadialMenu from './RadialMenu'; // RESTORED

// --- ICONS & STYLES ---

const getHazardIcon = (type: HazardType, color: string, size: number = 24) => {
    const props = { color, size };
    switch (type) {
        case HazardType.EARTHQUAKE: return <GiEarthCrack {...props} />;
        case HazardType.FIRE: return <GiWildfires {...props} />;
        case HazardType.TSUNAMI: return <GiBigWave {...props} />; // Fixed Name
        case HazardType.FLOOD: return <GiBigWave {...props} />;
        case HazardType.TORNADO: return <GiTornado {...props} />;
        case HazardType.BIOHAZARD: return <FaBiohazard {...props} />;
        case HazardType.SOS: return <GiHealthNormal {...props} />;
        case HazardType.TRAFFIC: return <RiTrafficLightFill {...props} />;
        case HazardType.CRASH: return <FaCarCrash {...props} />;
        case HazardType.POLICE: return <GiPoliceBadge {...props} />;
        case HazardType.CIVIL: return <GiRiotShield {...props} />;
        case HazardType.WEATHER: return <MdThunderstorm {...props} />;
        case HazardType.NUCLEAR: return <FaRadiation {...props} />;
        default: return <IoMdWarning {...props} />;
    }
};

const createHazardMarker = (type: HazardType, severity: string, coordinates: { lat: number, lng: number }, timestamp?: string) => {
    // Nature-Tech CSS Variable Colors
    let colorVar = 'var(--accent-primary)'; // Default blue
    let cssColor = '#60a5fa'; // Fallback for inline styles

    if (severity === 'critical') {
        colorVar = 'var(--accent-danger)';
        cssColor = '#ef4444';
    } else if (severity === 'high') {
        colorVar = 'var(--accent-alert)';
        cssColor = '#f59e0b';
    } else if (severity === 'medium') {
        colorVar = 'var(--accent-primary)';
        cssColor = '#60a5fa';
    }

    const iconMarkup = renderToStaticMarkup(getHazardIcon(type, '#fff', 18));
    const pulseAnimation = (severity === 'critical' || severity === 'high') ? 'animate-soft-pulse' : '';

    return L.divIcon({
        className: 'bg-transparent',
        html: `
        <div class="relative w-[40px] h-[40px] flex items-center justify-center group hover:scale-110 transition-all duration-300">
            <!-- Organic Glow Ring -->
            <div class="absolute inset-[-4px] rounded-full opacity-30 ${pulseAnimation}" 
                 style="background: radial-gradient(circle, ${cssColor} 0%, transparent 70%); filter: blur(8px);"></div>
            
            <!-- Main Circle -->
            <div class="absolute inset-0 rounded-full backdrop-blur-sm flex items-center justify-center transition-all"
                 style="background: rgba(2, 8, 23, 0.85); border: 2px solid ${cssColor}; box-shadow: 0 0 12px ${cssColor}40;">
                 <!-- Inner Glow -->
                 <div class="absolute inset-1 rounded-full opacity-20" style="background: ${cssColor};"></div>
            </div>

            <!-- Icon -->
            <div class="relative z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                ${iconMarkup}
            </div>

            <!-- Critical Indicator -->
            ${severity === 'critical' ? `
            <div class="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border border-black ${pulseAnimation}" 
                 style="background: ${cssColor};"></div>
            ` : ''}

            <!-- Label on Hover -->
            <div class="absolute -bottom-7 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                <div class="px-2 py-1 rounded-lg text-[9px] font-bold text-white uppercase tracking-wide shadow-lg"
                     style="background: rgba(2, 8, 23, 0.95); border: 1px solid ${cssColor}50;">
                    ${type}
                </div>
            </div>
        </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20]
    });
};

// --- HQ AVATAR GENERATORS ---
import { getEffectiveAvatar } from '../services/contactService';

const createContactIcon = (contact: Contact, isSelected: boolean) => {
    if (!contact) return L.divIcon({ className: '' });

    // Nature-Tech Status Colors (CSS Variables Fallbacks)
    const statusColors: Record<string, { var: string, hex: string }> = {
        [SafetyStatus.SAFE]: { var: 'var(--accent-safe)', hex: '#10b981' },
        [SafetyStatus.DANGER]: { var: 'var(--accent-danger)', hex: '#ef4444' },
        [SafetyStatus.SOS]: { var: 'var(--accent-danger)', hex: '#ef4444' },
        [SafetyStatus.WARNING]: { var: 'var(--accent-alert)', hex: '#f59e0b' },
        [SafetyStatus.HOME]: { var: 'var(--accent-primary)', hex: '#60a5fa' },
        [SafetyStatus.OFFLINE]: { var: 'var(--text-muted)', hex: '#64748b' }
    };

    const statusColor = statusColors[contact.status] || statusColors[SafetyStatus.SAFE];
    const avatar = getEffectiveAvatar(contact);

    return L.divIcon({
        className: 'bg-transparent',
        html: `
            <div class="relative w-[56px] h-[56px] flex items-center justify-center transition-all duration-200 ${isSelected ? 'scale-125 z-[100]' : 'hover:scale-110 z-[50]'}">
                ${isSelected ? `
                <div class="absolute inset-[-6px] rounded-full opacity-40 animate-soft-pulse" 
                     style="background: radial-gradient(circle, ${statusColor.hex} 0%, transparent 70%); filter: blur(6px);"></div>
                ` : ''}
                
                <!-- Main Avatar Container -->
                <div class="relative w-[44px] h-[44px] rounded-full overflow-hidden shadow-lg"
                     style="background: rgba(2, 8, 23, 0.9); border: 2px solid ${statusColor.hex};">
                    <img src="${avatar}" class="w-full h-full object-cover" />
                </div>

                <!-- Status Indicator -->
                ${contact.status === SafetyStatus.SOS ?
                `<div class="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 animate-soft-pulse" 
                     style="background: ${statusColor.hex}; border-color: var(--bg-deepest);"></div>` :
                `<div class="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 shadow-sm" 
                     style="background: ${statusColor.hex}; border-color: var(--bg-deepest);"></div>`
            }
                
                <!-- Name Label (on selection) -->
                ${isSelected ? `
                <div class="absolute -bottom-6 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full shadow-lg text-[10px] font-semibold whitespace-nowrap"
                     style="background: rgba(2, 8, 23, 0.95); color: var(--text-primary); border: 1px solid ${statusColor.hex}50;">
                    ${contact.name}
                </div>
                ` : ''}
            </div>
        `,
        iconSize: [56, 56],
        iconAnchor: [28, 28]
    });
};


const createUserIcon = (user: Contact | null) => {
    const avatar = getEffectiveAvatar(user);
    const cssColor = '#60a5fa'; // var(--accent-primary) fallback

    return L.divIcon({
        className: 'bg-transparent',
        html: `
        <div class="relative w-[64px] h-[64px] flex items-center justify-center z-[200]">
            <!-- Organic Breathing Glow -->
            <div class="absolute inset-0 rounded-full opacity-20 animate-breathe"
                 style="background: radial-gradient(circle, ${cssColor} 0%, transparent 70%); filter: blur(12px);"></div>
            <div class="absolute inset-[4px] rounded-full opacity-30 animate-soft-pulse"
                 style="border: 2px solid ${cssColor};"></div>
            
            <!-- Main Avatar -->
            <div class="relative w-[48px] h-[48px] rounded-full overflow-hidden shadow-xl"
                 style="background: rgba(2, 8, 23, 0.9); border: 3px solid ${cssColor};">
                <img src="${avatar}" class="w-full h-full object-cover" />
            </div>
            
            <!-- You Label -->
            <div class="absolute -bottom-6 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full shadow-xl text-[10px] font-bold whitespace-nowrap"
                 style="background: ${cssColor}; color: white;">
                YOU
            </div>
        </div>
        `,
        iconSize: [64, 64],
        iconAnchor: [32, 32]
    });
};

interface TacticalMapProps {
    userLocation: LatLng | null;
    userProfile?: Contact | null; // Added prop
    contacts: Contact[];
    nearbyUsers?: Contact[];
    hazards: Alert[];
    news?: NewsItem[];
    selectedId: string | null;
    selectedNewsId?: string | null; // Added
    onSelectContact: (id: string) => void;
    onSelectHazard: (hazard: Alert) => void;
    onSelectNews?: (itemId: string) => void; // Added
    onMapClick: (latlng: LatLng) => void;
    simulatedEvent?: Alert | null;
    isTargeting?: boolean;
    pulseSignals?: { contactId: string; status: string; timestamp: number }[];
    projectile?: { id: string; from: LatLng; to: LatLng; emoji: string; color: string } | null;
    onSelectSelf?: () => void;
    onContactHold?: (id: string, x: number, y: number) => void;
    focusOnUserTrigger?: number;
    flyToLocation?: LatLng | null;
    onOpenChat: (contactId: string) => void;
    onViewProfile: (contactId: string) => void;
    onSendPulse: (contactId: string) => void;
    onRegionChange?: (center: LatLng) => void;
    mapStyle?: 'SATELLITE' | 'DARK' | 'STREET';
    showTrafficLayer?: boolean; // TomTom traffic overlay toggle
    onAutoStyleChange?: (style: 'SATELLITE' | 'DARK' | 'STREET') => void; // New Prop for Auto-Switch
}

const TacticalMap: React.FC<TacticalMapProps> = ({
    userLocation,
    userProfile,
    contacts = [],
    nearbyUsers = [],
    hazards = [],
    news = [],
    selectedId,
    selectedNewsId, // Destructure
    onSelectContact,
    onSelectHazard,
    onSelectNews, // Destructure
    onMapClick,
    projectile,
    onSelectSelf,
    onContactHold,
    focusOnUserTrigger,
    flyToLocation,
    onOpenChat,
    onViewProfile,
    onSendPulse,
    onRegionChange,
    mapStyle = 'SATELLITE', // Default
    showTrafficLayer = true, // TomTom traffic enabled by default
    onAutoStyleChange
}) => {
    // DEBUG: Verify Data Flow
    useEffect(() => {
        console.log(`🗺️ TacticalMap Received: ${hazards.length} Hazards, ${news.length} News Items`);
    }, [hazards.length, news.length]);

    const mapRef = useRef<L.Map | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const markersLayerRef = useRef<L.LayerGroup | null>(null);
    const networkLayerRef = useRef<L.LayerGroup | null>(null);
    const hazardsLayerRef = useRef<L.MarkerClusterGroup | L.LayerGroup | null>(null);
    const newsLayerRef = useRef<L.LayerGroup | null>(null);
    const poiLayerRef = useRef<L.LayerGroup | null>(null); // New POI Layer
    const baseLayerRef = useRef<L.TileLayer | null>(null);
    const trafficFlowLayerRef = useRef<L.TileLayer | null>(null);
    const trafficIncidentLayerRef = useRef<L.TileLayer | null>(null);

    // --- TILE LOADING STATE ---
    const [tileLoadError, setTileLoadError] = useState(false);
    const [currentTileProvider, setCurrentTileProvider] = useState<string>('primary');

    // --- ROBUST TILE PROVIDERS WITH FALLBACKS ---
    const TILE_PROVIDERS = {
        SATELLITE: [
            {
                name: 'Esri Satellite',
                url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                options: { attribution: '© Esri', maxZoom: 19 }
            },
            {
                name: 'Google Satellite',
                url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                options: { attribution: '© Google', maxZoom: 20 }
            }
        ],
        DARK: [
            {
                name: 'CartoDB Dark',
                url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
                options: { attribution: '© CartoDB', maxZoom: 19, subdomains: 'abcd' }
            },
            {
                name: 'Stadia Dark',
                url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
                options: { attribution: '© Stadia', maxZoom: 20 }
            }
        ],
        STREET: [
            {
                name: 'OpenStreetMap',
                url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                options: { attribution: '© OpenStreetMap', maxZoom: 19, subdomains: 'abc' }
            }
        ]
    };

    // --- DYNAMIC MAP STYLE SWITCHING WITH ERROR HANDLING ---
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        console.log(`🗺️ Loading ${mapStyle} tiles...`);
        setTileLoadError(false);

        // 1. Remove existing base layer
        if (baseLayerRef.current) {
            map.removeLayer(baseLayerRef.current);
            baseLayerRef.current = null;
        }

        // 2. Get providers for current style
        let providers = TILE_PROVIDERS.SATELLITE; // Default
        if (mapStyle === 'DARK') providers = TILE_PROVIDERS.DARK;
        else if (mapStyle === 'STREET') providers = TILE_PROVIDERS.STREET;
        else if (mapStyle === 'SATELLITE') providers = TILE_PROVIDERS.SATELLITE;

        // 3. Try loading tiles with fallback chain
        let providerIndex = 0;
        let tileLoadTimeout: ReturnType<typeof setTimeout>;

        const tryLoadTiles = () => {
            if (providerIndex >= providers.length) {
                console.error('❌ All tile providers failed');
                setTileLoadError(true);
                // Use basic dark background as last resort
                map.getContainer().style.background = '#0a0f1e';
                return;
            }

            const provider = providers[providerIndex];
            console.log(`🔄 Trying ${provider.name}...`);
            setCurrentTileProvider(provider.name);

            try {
                const tileLayer = L.tileLayer(provider.url, {
                    ...provider.options,
                    errorTileUrl: '', // Prevent red error tiles
                });

                // Error handling - try next provider if tiles fail
                let errorCount = 0;
                const MAX_ERRORS = 5;

                tileLayer.on('tileerror', () => {
                    errorCount++;
                    if (errorCount >= MAX_ERRORS) {
                        console.warn(`⚠️ ${provider.name} tile errors, trying fallback...`);
                        clearTimeout(tileLoadTimeout);
                        map.removeLayer(tileLayer);
                        providerIndex++;
                        tryLoadTiles();
                    }
                });

                tileLayer.on('tileload', () => {
                    // Success - clear error timeout
                    clearTimeout(tileLoadTimeout);
                    console.log(`✅ ${provider.name} tiles loading successfully`);
                    setTileLoadError(false);
                });

                baseLayerRef.current = tileLayer;
                tileLayer.addTo(map);
                tileLayer.bringToBack();

                // Timeout fallback - if no tiles load in 5 seconds, try next provider
                tileLoadTimeout = setTimeout(() => {
                    if (errorCount > 0 || !map.hasLayer(tileLayer)) {
                        console.warn(`⏱️ ${provider.name} timeout, trying fallback...`);
                        if (map.hasLayer(tileLayer)) {
                            map.removeLayer(tileLayer);
                        }
                        providerIndex++;
                        tryLoadTiles();
                    }
                }, 5000);

            } catch (error) {
                console.error(`❌ Error creating tile layer:`, error);
                providerIndex++;
                tryLoadTiles();
            }
        };

        tryLoadTiles();

        return () => {
            clearTimeout(tileLoadTimeout);
        };

    }, [mapStyle]);

    // --- TOMTOM TRAFFIC LAYER TOGGLE ---
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Toggle Traffic Flow Layer
        if (trafficFlowLayerRef.current) {
            if (showTrafficLayer) {
                if (!map.hasLayer(trafficFlowLayerRef.current)) {
                    trafficFlowLayerRef.current.addTo(map);
                }
            } else {
                if (map.hasLayer(trafficFlowLayerRef.current)) {
                    map.removeLayer(trafficFlowLayerRef.current);
                }
            }
        }

        // Toggle Traffic Incidents Layer
        if (trafficIncidentLayerRef.current) {
            if (showTrafficLayer) {
                if (!map.hasLayer(trafficIncidentLayerRef.current)) {
                    trafficIncidentLayerRef.current.addTo(map);
                }
            } else {
                if (map.hasLayer(trafficIncidentLayerRef.current)) {
                    map.removeLayer(trafficIncidentLayerRef.current);
                }
            }
        }
    }, [showTrafficLayer]);

    // POI State
    const [emergencyPois, setEmergencyPois] = useState<any[]>([]);

    // Load POIs on mount/region change
    useEffect(() => {
        if (!userLocation) return;
        import('../services/tomtomService').then(async ({ searchNearbyEmergencyPOIs }) => {
            try {
                const pois = await searchNearbyEmergencyPOIs(userLocation, 15); // 15km radius
                setEmergencyPois(pois);
            } catch (e) {
                console.warn("Failed to load POIs", e);
            }
        });
    }, [userLocation?.lat, userLocation?.lng]); // Re-fetch when user moves significantly

    // Radial Menu State
    const [radialMenu, setRadialMenu] = React.useState<{ x: number, y: number, contactId: string } | null>(null);
    const [zoomLevel, setZoomLevel] = React.useState(13); // Track zoom for clustering
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const startHold = (contactId: string, e: any) => {
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);

        // Leaflet event normalization
        const point = e.containerPoint;

        longPressTimerRef.current = setTimeout(() => {
            if (point) {
                // Boundary Check: prevent menu from being clipped
                if (containerRef.current) {
                    const { clientWidth, clientHeight } = containerRef.current;
                    const margin = 100; // Radius + Padding

                    const safeX = Math.min(Math.max(point.x, margin), clientWidth - margin);
                    const safeY = Math.min(Math.max(point.y, margin), clientHeight - margin);

                    setRadialMenu({ x: safeX, y: safeY, contactId });
                } else {
                    setRadialMenu({ x: point.x, y: point.y, contactId });
                }
            }
        }, 600);
    };

    const cancelHold = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const handleRadialAction = (action: string) => {
        if (!radialMenu) return;
        const { contactId } = radialMenu;
        console.log(`Radial Action: ${action} on ${contactId}`);

        switch (action) {
            case 'MESSAGE':
                onOpenChat(contactId);
                break;
            case 'PROFILE':
                onViewProfile(contactId);
                break;
            case 'PULSE':
            case 'HEART':
                onSendPulse(contactId);
                break;
        }
        setRadialMenu(null);
    };

    // Handle FlyTo Requests
    useEffect(() => {
        if (mapRef.current && flyToLocation) {
            mapRef.current.flyTo([flyToLocation.lat, flyToLocation.lng], 16, {
                animate: true,
                duration: 1.5
            });
        }
    }, [flyToLocation]);

    // Focus Effect + Initial Center
    const hasInitialCentered = useRef(false);
    useEffect(() => {
        // Initial centering when user location first becomes available
        if (!hasInitialCentered.current && userLocation && mapRef.current) {
            mapRef.current.flyTo([userLocation.lat, userLocation.lng], 15, {
                animate: true,
                duration: 1.5
            });
            hasInitialCentered.current = true;
        }
        // Manual focus trigger
        if (focusOnUserTrigger && userLocation && mapRef.current) {
            mapRef.current.flyTo([userLocation.lat, userLocation.lng], 16, {
                animate: true,
                duration: 1.5
            });
        }
    }, [focusOnUserTrigger, userLocation]);

    // Using ref for props to avoid closure staleness in event listeners
    // Refs for Stale Closures (Leaflet Events)
    const propsRef = useRef({
        onSelectContact,
        onSelectHazard,
        onSelectNews, // Added
        onMapClick,
        onSelectSelf,
        onContactHold,
        onRegionChange,
        onAutoStyleChange // Capture latest prop
    });

    // Update ref on every render
    propsRef.current = {
        onSelectContact,
        onSelectHazard,
        onSelectNews, // Added
        onMapClick,
        onSelectSelf,
        onContactHold,
        onRegionChange,
        onAutoStyleChange
    };



    // --- INITIALIZATION ---
    useEffect(() => {
        if (!containerRef.current) return;
        if (mapRef.current) return; // Prevent double init

        try {
            const map = L.map(containerRef.current, {
                center: [48.8566, 2.3522], // Default Paris
                zoom: 13,
                zoomControl: false,
                attributionControl: false,
                dragging: true,
                doubleClickZoom: false,
                scrollWheelZoom: true,
                bounceAtZoomLimits: false, // Mobile smoothness
            });

            // FORCE INVALIDATE ON LOAD FOR MOBILE
            setTimeout(() => {
                map.invalidateSize();
            }, 100);

            // BASE LAYER MANAGEMENT
            // We use a separate effect for style changes, but we need to init the ref here if null
            if (!baseLayerRef.current) {
                // Initial load logic will be handled by the useEffect below
                // But we add a placeholder to ensure avoiding race conditions if needed
            }

            networkLayerRef.current = L.layerGroup().addTo(map);
            // REPLACED CLUSTERING WITH RAW LAYER to show Radial Groups
            markersLayerRef.current = L.layerGroup().addTo(map);

            // --- TOMTOM TRAFFIC LAYERS ---
            // Traffic Flow Layer (shows real-time speed/congestion as colored roads)
            try {
                const flowTileUrl = getTrafficFlowTileUrl();
                if (flowTileUrl) {
                    trafficFlowLayerRef.current = L.tileLayer(flowTileUrl, {
                        attribution: '&copy; TomTom Traffic',
                        maxZoom: 22,
                        opacity: 0.8
                    });
                    // Add if showTrafficLayer is enabled
                    if (showTrafficLayer) {
                        trafficFlowLayerRef.current.addTo(map);
                    }
                    console.log('✅ TomTom Traffic Flow Layer initialized');
                }

                // Traffic Incidents Layer (shows accident/roadwork icons as tiles)
                const incidentTileUrl = getTrafficIncidentTileUrl();
                if (incidentTileUrl) {
                    trafficIncidentLayerRef.current = L.tileLayer(incidentTileUrl, {
                        attribution: '&copy; TomTom Incidents',
                        maxZoom: 22,
                        opacity: 0.9
                    });
                    if (showTrafficLayer) {
                        trafficIncidentLayerRef.current.addTo(map);
                    }
                    console.log('✅ TomTom Traffic Incidents Layer initialized');
                }
            } catch (err) {
                console.warn('⚠️ TomTom traffic layers failed to initialize:', err);
            }

            // HAZARDS LAYER - Safe Cluster Init
            // Enable Clustering
            const hasCluster = true;

            if (hasCluster && (L as any).markerClusterGroup) {
                // @ts-ignore
                hazardsLayerRef.current = (L as any).markerClusterGroup({
                    showCoverageOnHover: false,
                    spiderfyOnMaxZoom: true,
                    zoomToBoundsOnClick: true,
                    removeOutsideVisibleBounds: true,
                    animate: true,
                    maxClusterRadius: 60, // Tighter clusters for better accuracy

                    // CUSTOM CLUSTER ICON LOGIC
                    iconCreateFunction: function (cluster: any) {
                        const childMarkers = cluster.getAllChildMarkers();
                        const count = childMarkers.length;

                        // 1. Analyze Categories
                        const typeCounts: Record<string, number> = {};
                        let maxType = 'ALERT';
                        let maxCount = 0;
                        let hasCritical = false;

                        childMarkers.forEach((marker: any) => {
                            const h = marker.hazardData as Alert;
                            if (h) {
                                typeCounts[h.type] = (typeCounts[h.type] || 0) + 1;
                                if (typeCounts[h.type] > maxCount) {
                                    maxCount = typeCounts[h.type];
                                    maxType = h.type;
                                }
                                if (h.severity === 'critical') hasCritical = true;
                            }
                        });

                        // 2. Determine Style based on Dominant Type (Nature-Tech Colors)
                        let cssColor = '#60a5fa'; // var(--accent-primary)
                        let iconEmoji = '⚠️';

                        // Map Types to Nature-Tech Colors
                        switch (maxType) {
                            case 'TRAFFIC': cssColor = '#f59e0b'; iconEmoji = '🚗'; break; // var(--accent-alert)
                            case 'FIRE': cssColor = '#ef4444'; iconEmoji = '🔥'; break; // var(--accent-danger)
                            case 'FLOOD': cssColor = '#60a5fa'; iconEmoji = '🌊'; break; // var(--accent-primary)
                            case 'EARTHQUAKE': cssColor = '#f59e0b'; iconEmoji = '📉'; break; // var(--accent-alert)
                            case 'BIOHAZARD': cssColor = '#10b981'; iconEmoji = '☣️'; break; // var(--accent-safe)
                            case 'WEATHER': cssColor = '#64748b'; iconEmoji = '⛈️'; break; // var(--text-muted)
                            default: cssColor = '#64748b'; iconEmoji = '⚠️';
                        }

                        if (hasCritical) {
                            cssColor = '#ef4444'; // var(--accent-danger)
                            iconEmoji = '‼️';
                        }

                        const pulseClass = hasCritical ? 'animate-soft-pulse' : '';

                        // 3. Return Custom Icon with Nature-Tech Design
                        return new L.DivIcon({
                            html: `
                                <div class="relative flex items-center justify-center w-12 h-12 transition-all duration-300 hover:scale-110">
                                    <!-- Organic Glow Ring -->
                                    <div class="absolute inset-[-2px] rounded-full opacity-30"
                                         style="background: radial-gradient(circle, ${cssColor} 0%, transparent 70%); filter: blur(8px);"></div>
                                    
                                    <!-- Rotating Border (if critical) -->
                                    ${hasCritical ? `<div class="absolute inset-0 rounded-full border-2 border-dashed animate-[spin_10s_linear_infinite]" 
                                         style="border-color: ${cssColor}; opacity: 0.4;"></div>` : ''}

                                    <!-- Core Bubble -->
                                    <div class="relative flex items-center justify-center w-full h-full rounded-full backdrop-blur-sm border-2 shadow-lg ${pulseClass}"
                                         style="background: rgba(2, 8, 23, 0.9); border-color: ${cssColor}; box-shadow: 0 0 16px ${cssColor}30;">
                                        
                                        <!-- Icon & Count Stack -->
                                        <div class="flex flex-col items-center justify-center leading-none">
                                            <span class="text-sm mb-0.5 filter drop-shadow-lg">${iconEmoji}</span>
                                            <span class="text-[11px] font-bold font-mono" style="color: ${cssColor};">${count}</span>
                                        </div>
                                    </div>

                                    <!-- Category Label -->
                                    <div class="absolute -bottom-5 px-1.5 py-0.5 rounded border text-[8px] font-bold uppercase tracking-wider whitespace-nowrap"
                                         style="background: rgba(2, 8, 23, 0.95); border-color: ${cssColor}50; color: var(--text-primary);">
                                        ${maxType}
                                    </div>
                                </div>
                            `,
                            className: 'bg-transparent',
                            iconSize: new L.Point(48, 48),
                            iconAnchor: [24, 24]
                        });
                    }
                });
            } else {
                console.warn("MarkerCluster plugin missing or disabled. Falling back to LayerGroup.");
                hazardsLayerRef.current = L.layerGroup();
            }
            map.addLayer(hazardsLayerRef.current);

            // --- SMART REGION SYNCING ---
            map.on('moveend', () => {
                const center = map.getCenter();
                if (propsRef.current.onRegionChange) {
                    propsRef.current.onRegionChange({ lat: center.lat, lng: center.lng });
                }
            });

            map.on('click', (e) => {
                // Ensure callback exists
                if (propsRef.current.onMapClick) {
                    propsRef.current.onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
                }
            });

            // Re-render markers on zoom to recalculate clustering
            map.on('zoomend', () => {
                const z = map.getZoom();
                setZoomLevel(z);

                // AUTO-SWITCH LOGIC
                if (propsRef.current.onAutoStyleChange) {
                    if (z > 16 && mapStyle === 'SATELLITE') {
                        propsRef.current.onAutoStyleChange('STREET');
                    } else if (z <= 16 && mapStyle !== 'SATELLITE') {
                        propsRef.current.onAutoStyleChange('SATELLITE');
                    }
                }
            });

            mapRef.current = map;
        } catch (error) {
            console.error("Map Initialization Error:", error);
        }

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // FIX: Force Map Resize Calculation on Mount
    useEffect(() => {
        const timer = setTimeout(() => {
            if (mapRef.current) {
                mapRef.current.invalidateSize();
            }
        }, 200);
        return () => clearTimeout(timer);
    }, []);

    // --- TOMTOM TRAFFIC LAYER TOGGLE ---
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Toggle Traffic Flow Layer
        if (trafficFlowLayerRef.current) {
            if (showTrafficLayer) {
                if (!map.hasLayer(trafficFlowLayerRef.current)) {
                    trafficFlowLayerRef.current.addTo(map);
                }
            } else {
                if (map.hasLayer(trafficFlowLayerRef.current)) {
                    map.removeLayer(trafficFlowLayerRef.current);
                }
            }
        }

        // Toggle Traffic Incidents Layer
        if (trafficIncidentLayerRef.current) {
            if (showTrafficLayer) {
                if (!map.hasLayer(trafficIncidentLayerRef.current)) {
                    trafficIncidentLayerRef.current.addTo(map);
                }
            } else {
                if (map.hasLayer(trafficIncidentLayerRef.current)) {
                    map.removeLayer(trafficIncidentLayerRef.current);
                }
            }
        }
    }, [showTrafficLayer]);

    // --- UPDATES ---
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Debounced Update Frame
        const updateFrame = requestAnimationFrame(() => {
            // Clear layers safety check
            if (markersLayerRef.current) markersLayerRef.current.clearLayers();
            if (networkLayerRef.current) networkLayerRef.current.clearLayers();
            if (hazardsLayerRef.current) hazardsLayerRef.current.clearLayers();

            try {
                // Main Update Logic Wrapper
                const allContacts = [...(contacts || []), ...(nearbyUsers || [])];

                // 1. DRAW NETWORK CONNECTIONS (User -> Contacts)
                if (userLocation && userLocation.lat !== 0) {
                    const userLatLng = L.latLng(userLocation.lat, userLocation.lng);

                    allContacts.forEach(contact => {
                        if (!contact || !contact.coordinates || (contact.coordinates.lat === 0 && contact.coordinates.lng === 0)) return;

                        const contactLatLng = L.latLng(contact.coordinates.lat, contact.coordinates.lng);

                        const line = L.polyline([userLatLng, contactLatLng], {
                            color: '#00F0FF',
                            weight: 1,
                            opacity: 0.3,
                            dashArray: '5, 10',
                            className: 'network-line animate-dash'
                        });
                        if (networkLayerRef.current) networkLayerRef.current.addLayer(line);
                    });
                }

                // 2. CONTACT & USER MARKERS WITH DYNAMIC SPIDERFY (Pixel-based)
                // Combine User and Contacts to prevent ANY overlap
                type RenderableEntity = { type: 'user' | 'contact', data: Contact };
                let entities: RenderableEntity[] = [];

                if (userProfile && userLocation && userLocation.lat !== 0) {
                    entities.push({ type: 'user', data: { ...userProfile, coordinates: userLocation } });
                }

                allContacts.forEach(c => {
                    if (c && c.coordinates && (c.coordinates.lat !== 0 || c.coordinates.lng !== 0)) {
                        entities.push({ type: 'contact', data: c });
                    }
                });

                // Grouping Logic (Pixel Space)
                const OVERLAP_RADIUS_PX = 60; // Pixels
                const SPIDER_RADIUS_PX = 70;  // Pixels to spread out
                const processed = new Set<string>();
                const clusters: RenderableEntity[][] = [];

                entities.forEach(entity => {
                    if (processed.has(entity.data.id)) return;

                    const entityLatLng = L.latLng(entity.data.coordinates.lat, entity.data.coordinates.lng);
                    const entityPoint = map.latLngToLayerPoint(entityLatLng);

                    const cluster = entities.filter(other => {
                        if (processed.has(other.data.id)) return false;
                        const otherLatLng = L.latLng(other.data.coordinates.lat, other.data.coordinates.lng);
                        const otherPoint = map.latLngToLayerPoint(otherLatLng);
                        return entityPoint.distanceTo(otherPoint) < OVERLAP_RADIUS_PX;
                    });

                    cluster.forEach(e => processed.add(e.data.id));
                    if (cluster.length > 0) clusters.push(cluster);
                });

                // Render Clusters
                // Render Clusters
                clusters.forEach(cluster => {
                    const centerLat = cluster.reduce((sum, e) => sum + e.data.coordinates.lat, 0) / cluster.length;
                    const centerLng = cluster.reduce((sum, e) => sum + e.data.coordinates.lng, 0) / cluster.length;
                    const centerLatLng = L.latLng(centerLat, centerLng);
                    const centerPoint = map.latLngToLayerPoint(centerLatLng);

                    // LOGIC FIX: Only Spiderfy (Explode) at high zoom (>15).
                    // At low zoom, show a single "Squad" marker to prevent "Different Country" offset artifacts.
                    if (cluster.length > 1 && zoomLevel < 16) {
                        // --- RENDER CLUSTER MARKER ---
                        const count = cluster.length;
                        const hasUser = cluster.some(e => e.type === 'user');
                        const color = hasUser ? '#00F0FF' : '#10b981'; // Cyan if user included, else Green

                        const icon = L.divIcon({
                            className: 'bg-transparent',
                            html: `
                        <div class="relative flex items-center justify-center w-14 h-14 group hover:scale-110 transition-transform cursor-pointer">
                             <!-- Server Node Rings -->
                             <div class="absolute inset-0 bg-black/80 backdrop-blur-md rounded-lg border border-[${color}] shadow-[0_0_20px_${color}50]"></div>
                             <div class="absolute -inset-1 border border-[${color}]/30 rounded-xl animate-pulse"></div>
                             
                             <!-- Data Stream Effect -->
                             <div class="absolute inset-0 overflow-hidden rounded-lg opacity-30">
                                <div class="w-full h-full bg-gradient-to-t from-[${color}] to-transparent animate-scan"></div>
                             </div>

                             <div class="relative flex flex-col items-center justify-center z-10 space-y-0.5">
                                <div class="flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-[${color}] animate-pulse"></span>
                                    <span class="text-[10px] font-black text-[${color}] font-mono tracking-widest">NET</span>
                                </div>
                                <span class="text-lg font-bold text-white leading-none">${count}</span>
                             </div>

                             <!-- Connector Lines Decoration -->
                             <div class="absolute -left-2 top-1/2 w-2 h-[1px] bg-[${color}]/50"></div>
                             <div class="absolute -right-2 top-1/2 w-2 h-[1px] bg-[${color}]/50"></div>

                             <div class="absolute -bottom-5 bg-black/90 px-2 py-0.5 rounded-sm text-[8px] font-bold text-[${color}] border border-[${color}]/30 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider">
                                SQUAD_CLUSTER
                             </div>
                        </div>
                        `,
                            iconSize: [56, 56],
                            iconAnchor: [28, 28]
                        });

                        const marker = L.marker(centerLatLng, { icon, zIndexOffset: 3000 });
                        marker.on('click', (e) => {
                            L.DomEvent.stopPropagation(e);
                            // Fly to the cluster to break it apart
                            map.flyTo(centerLatLng, 17, { animate: true, duration: 1 });
                        });
                        if (markersLayerRef.current) markersLayerRef.current.addLayer(marker);

                    } else {
                        // --- RENDER INDIVIDUAL SPIDERFIED MARKERS (High Zoom) ---
                        cluster.forEach((entity, index) => {
                            let finalLatLng = L.latLng(entity.data.coordinates.lat, entity.data.coordinates.lng);

                            // Apply Spiderfy Offset if clustered (only if > 1, though logic enters here if count=1 too via the else, wait no. cluster loop logic)
                            // Actually clusters can have length 1.
                            if (cluster.length > 1) {
                                const angle = (2 * Math.PI * index) / cluster.length;
                                const offsetX = Math.cos(angle) * SPIDER_RADIUS_PX;
                                const offsetY = Math.sin(angle) * SPIDER_RADIUS_PX;
                                const newPoint = centerPoint.add(L.point(offsetX, offsetY));
                                finalLatLng = map.layerPointToLatLng(newPoint);
                            }

                            // Render User or Contact
                            if (entity.type === 'user') {
                                const icon = createUserIcon(entity.data);
                                const marker = L.marker(finalLatLng, { icon, zIndexOffset: 2000 });
                                marker.on('click', (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    if (propsRef.current.onSelectSelf) propsRef.current.onSelectSelf();
                                });
                                if (markersLayerRef.current) markersLayerRef.current.addLayer(marker);
                            } else {
                                const isSelected = selectedId === entity.data.id;
                                const icon = createContactIcon(entity.data, isSelected);
                                const marker = L.marker(finalLatLng, { icon, zIndexOffset: isSelected ? 1000 : 0 });

                                marker.on('click', (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    if (propsRef.current.onSelectContact) propsRef.current.onSelectContact(entity.data.id);
                                });

                                // Long Press
                                marker.on('mousedown', (e) => startHold(entity.data.id, e as any));
                                marker.on('touchstart', (e) => startHold(entity.data.id, e as any));
                                marker.on('mouseup', cancelHold);
                                marker.on('touchend', cancelHold);

                                if (markersLayerRef.current) markersLayerRef.current.addLayer(marker);
                            }
                        });
                    }
                });

                // 3. HAZARDS - Rendered on separate layer
                try {
                    if (zoomLevel < 11) {
                        // --- LOW ZOOM: CATEGORY CAPSULES ---
                        // Group by Type AND Proximity (City/Region)
                        const hazardGroups: Record<string, Alert[]> = {};

                        (hazards || []).forEach(h => {
                            if (!h || !h.coordinates) return;
                            // Group key: "TYPE_LAT_LNG_BUCKET"
                            const key = `${h.type}_${Math.round(h.coordinates.lat * 2) / 2}_${Math.round(h.coordinates.lng * 2) / 2}`;
                            if (!hazardGroups[key]) hazardGroups[key] = [];
                            hazardGroups[key].push(h);
                        });

                        Object.values(hazardGroups).forEach(group => {
                            const count = group.length;
                            if (count === 0) return;
                            const h = group[0]; // Representative
                            const centerLat = group.reduce((sum, e) => sum + e.coordinates.lat, 0) / count;
                            const centerLng = group.reduce((sum, e) => sum + e.coordinates.lng, 0) / count;

                            // Type-Specific Colors
                            let color = '#3b82f6';
                            if (h.type === HazardType.FIRE) color = '#f97316';
                            else if (h.type === HazardType.EARTHQUAKE) color = '#a855f7';
                            else if (h.type === HazardType.TRAFFIC) color = '#f59e0b';
                            else if (h.type === HazardType.BIOHAZARD) color = '#ef4444';
                            else if (h.type === HazardType.CIVIL) color = '#ef4444';
                            else if (h.type === HazardType.WEATHER) color = '#64748b'; // Fixed color var ref

                            const icon = L.divIcon({
                                className: 'bg-transparent',
                                html: `
                            <div class="relative flex items-center justify-center p-1.5 group cursor-pointer transition-transform hover:scale-110">
                                <div class="absolute inset-0 bg-black/80 backdrop-blur border border-[${color}]/50 rounded pointer-events-none shadow-[0_0_15px_${color}40]"></div>
                                <div class="relative flex items-center gap-2 px-2">
                                     <div class="w-2 h-2 rounded-full bg-[${color}] animate-pulse shadow-[0_0_5px_${color}]"></div>
                                     <div class="text-[9px] font-black text-white uppercase tracking-wider whitespace-nowrap">
                                         ${h.type} <span class="text-[${color}]">(${count})</span>
                                     </div>
                                </div>
                            </div>
                            `,
                                iconSize: [100, 24],
                                iconAnchor: [50, 12]
                            });

                            const marker = L.marker([centerLat, centerLng], { icon, zIndexOffset: 2500 });
                            marker.on('click', (e) => {
                                L.DomEvent.stopPropagation(e);
                                const bounds = L.latLngBounds(group.map(e => [e.coordinates.lat, e.coordinates.lng]));
                                map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 14, duration: 1.5 });
                            });
                            if (hazardsLayerRef.current) hazardsLayerRef.current.addLayer(marker);
                        });

                    } else {
                        // --- HIGH ZOOM: INDIVIDUAL MARKERS (Existing Logic) ---
                        (hazards || []).forEach(hazard => {
                            if (!hazard || !hazard.coordinates || (hazard.coordinates.lat === 0 && hazard.coordinates.lng === 0)) return;

                            const icon = createHazardMarker(hazard.type, hazard.severity, hazard.coordinates, hazard.timestamp || hazard.time);
                            const marker = L.marker([hazard.coordinates.lat, hazard.coordinates.lng], { icon });

                            // Attach data for cluster analysis
                            (marker as any).hazardData = hazard;

                            marker.on('click', (e: any) => {
                                L.DomEvent.stopPropagation(e);
                                // Spiderfy handling is automatic in clusters, but for single markers:
                                if (propsRef.current.onSelectHazard) propsRef.current.onSelectHazard(hazard);
                            });

                            // Add to HAZARDS layer
                            if (hazardsLayerRef.current) hazardsLayerRef.current.addLayer(marker);
                        });
                    }
                } catch (err) {
                    console.error("Critical Hazard Render Failure:", err);
                }

                // 4. NEWS & INTEL - Rendered on separate layer with CLUTTER AVOIDANCE
                if (!newsLayerRef.current) {
                    newsLayerRef.current = L.layerGroup().addTo(mapRef.current);
                } else {
                    newsLayerRef.current.clearLayers();
                }

                const OCCUPANCY_RADIUS = 45; // Pixels distance check
                const occupiedZones: { x: number, y: number }[] = [];

                // Pre-fill occupied zones from rendered clusters/contacts to avoid overlap
                // Re-calculate positions used in step 2
                clusters.forEach(cluster => {
                    const centerLat = cluster.reduce((sum, e) => sum + e.data.coordinates.lat, 0) / cluster.length;
                    const centerLng = cluster.reduce((sum, e) => sum + e.data.coordinates.lng, 0) / cluster.length;
                    const point = map.latLngToLayerPoint(L.latLng(centerLat, centerLng));
                    occupiedZones.push({ x: point.x, y: point.y });

                    if (cluster.length > 1 && zoomLevel >= 16) {
                        // Spiderfied items also occupy space
                        cluster.forEach((_, index) => {
                            const angle = (2 * Math.PI * index) / cluster.length;
                            const offsetX = Math.cos(angle) * SPIDER_RADIUS_PX;
                            const offsetY = Math.sin(angle) * SPIDER_RADIUS_PX;
                            occupiedZones.push({ x: point.x + offsetX, y: point.y + offsetY });
                        });
                    }
                });


                (news || []).forEach(item => {
                    if (!item || !item.coordinates) return;

                    // COLLISION CHECK: Skip News items that overlap with SQUAD/USER
                    const itemPoint = map.latLngToLayerPoint(L.latLng(item.coordinates.lat, item.coordinates.lng));
                    const isOverlapping = occupiedZones.some(zone => {
                        const dx = zone.x - itemPoint.x;
                        const dy = zone.y - itemPoint.y;
                        return Math.sqrt(dx * dx + dy * dy) < OCCUPANCY_RADIUS;
                    });

                    if (isOverlapping) {
                        // Optional: Could render a tiny dot instead? 
                        // For now, simple strict culling for clarity.
                        return;
                    }

                    // Add to occupied zones so News doesn't overlap News
                    occupiedZones.push({ x: itemPoint.x, y: itemPoint.y });

                    const isTraffic = item.category === 'TRAFFIC';
                    const isWeather = item.category === 'WEATHER';
                    const isSelected = selectedNewsId === item.id; // Check selection

                    // Icon Selection
                    let iconHtml = '';
                    let color = '#3b82f6'; // Blue default

                    if (isTraffic) {
                        color = '#f59e0b'; // Amber
                        try {
                            iconHtml = renderToStaticMarkup(<Car color={color} size={20} />);
                        } catch (e) {
                            console.error("News Icon Render Failed", e);
                            iconHtml = '🚗';
                        }
                    } else if (isWeather) {
                        color = '#94a3b8'; // Slate
                        try {
                            iconHtml = renderToStaticMarkup(<CloudLightning color={color} size={20} />);
                        } catch (e) {
                            console.error("News Icon Render Failed", e);
                            iconHtml = '⛈️';
                        }
                    } else {
                        // Generic News
                        try {
                            iconHtml = renderToStaticMarkup(<Info color={color} size={20} />);
                        } catch (e) {
                            console.error("News Icon Render Failed", e);
                            iconHtml = 'ℹ️';
                        }
                    }

                    const icon = L.divIcon({
                        className: 'bg-transparent',
                        html: `
                    <div class="relative flex items-center justify-center animate-fade-in group">
                        <div class="relative flex items-center justify-center rounded-lg shadow-lg cursor-pointer hover:scale-110 transition-transform bg-[#0f172a]/90 border ${isSelected ? 'border-white' : 'border-white/10'}"
                             style="width: 36px; height: 36px; padding: 6px; box-shadow: 0 0 15px ${color}40;">
                            ${iconHtml}
                        </div>
                        <div class="absolute -bottom-1 w-1 h-1 bg-${color} rounded-full"></div>
                         ${isSelected ? `<div class="absolute -top-1 -right-1 w-2 h-2 bg-${color} rounded-full animate-ping"></div>` : ''}
                    </div>
                `,
                        iconSize: [36, 36],
                        iconAnchor: [18, 18]
                    });

                    const marker = L.marker([item.coordinates.lat, item.coordinates.lng], { icon });

                    marker.on('click', (e) => {
                        L.DomEvent.stopPropagation(e);
                        if (propsRef.current.onSelectNews) {
                            propsRef.current.onSelectNews(item.id);
                        }
                    });

                    if (newsLayerRef.current) newsLayerRef.current.addLayer(marker);
                });

                // 5. EMERGENCY POIs - Rendered on separate layer
                if (!poiLayerRef.current) {
                    poiLayerRef.current = L.layerGroup().addTo(mapRef.current);
                } else {
                    poiLayerRef.current.clearLayers();
                }

                emergencyPois.forEach(poi => {
                    if (!poi || !poi.coordinates) return;

                    let iconChar = '🏥';
                    let color = '#ef4444'; // Red for Hospital

                    if (poi.type === 'POLICE') { iconChar = '👮'; color = '#3b82f6'; } // Blue
                    if (poi.type === 'FIRE_STATION') { iconChar = '🚒'; color = '#f97316'; } // Orange
                    if (poi.type === 'PHARMACY') { iconChar = '💊'; color = '#10b981'; } // Green

                    const icon = L.divIcon({
                        className: 'bg-transparent',
                        html: `
                        <div class="relative flex flex-col items-center group">
                            <div class="w-8 h-8 rounded-full bg-[#0F172A] border-2 flex items-center justify-center shadow-lg transition-transform hover:scale-125 hover:z-50"
                                 style="border-color: ${color}; box-shadow: 0 0 10px ${color}40;">
                                <span class="text-sm">${iconChar}</span>
                            </div>
                            <div class="absolute -bottom-6 opacity-0 group-hover:opacity-100 transition-opacity bg-black/90 text-white text-[9px] px-2 py-1 rounded whitespace-nowrap border border-white/10 z-50 pointer-events-none">
                                ${poi.name}
                            </div>
                        </div>
                    `,
                        iconSize: [32, 32],
                        iconAnchor: [16, 16]
                    });

                    const marker = L.marker([poi.coordinates.lat, poi.coordinates.lng], { icon });
                    marker.bindPopup(`
                    <div style="min-width: 150px; text-align: center;">
                        <h3 style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">${poi.name}</h3>
                        <p style="font-size: 12px; color: #666; margin-bottom: 8px;">${poi.address}</p>
                        ${poi.phone ? `<a href="tel:${poi.phone}" style="display: inline-block; background: ${color}; color: white; padding: 4px 8px; border-radius: 4px; text-decoration: none; font-size: 11px;">CALL</a>` : ''}
                        <button onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${poi.coordinates.lat},${poi.coordinates.lng}', '_blank')" 
                                style="background: #333; color: white; padding: 4px 8px; border-radius: 4px; border: none; font-size: 11px; margin-left: 4px; cursor: pointer;">
                            NAVIGATE
                        </button>
                    </div>
                `);

                    if (poiLayerRef.current) poiLayerRef.current.addLayer(marker);
                });

                // 4. PROJECTILE ANIMATION
                if (projectile && projectile.from && projectile.to) {
                    const icon = L.divIcon({
                        className: 'bg-transparent',
                        html: `<div style="font-size: 24px; animation: fly 1s linear forwards;">${projectile.emoji}</div>`,
                        iconSize: [30, 30]
                    });

                    // Create a moving marker? Or just CSS animation on a fixed path?
                    // Leaflet doesn't handle "flying" markers natively easily without a plugin or custom loop.
                    // Simplified: Draw a line and a marker at the 'to' position that fades in?
                    // Better: Use a CSS animation on the marker itself, but we need start/end pixels.

                    // Alternative: Simply place the marker at 'to' and animate it "hitting".
                    const hitMarker = L.marker([projectile.to.lat, projectile.to.lng], {
                        icon: L.divIcon({
                            className: 'bg-transparent',
                            html: `
                        <div class="relative w-full h-full flex items-center justify-center">
                            <div class="absolute inset-0 rounded-full animate-ping opacity-75" style="background: ${projectile.color}"></div>
                            <div class="text-2xl animate-bounce">${projectile.emoji}</div>
                        </div>
                    `,
                            iconSize: [40, 40]
                        })
                    });

                    if (markersLayerRef.current) markersLayerRef.current.addLayer(hitMarker);
                }

            } catch (err) {
                console.error("TacticalMap Update Error:", err);
            }
        });

        // Cleanup
        return () => cancelAnimationFrame(updateFrame);
    }, [userLocation, contacts, nearbyUsers, hazards, selectedId, projectile, zoomLevel]);

    return (
        <div className="w-full h-full relative bg-[#020817] overflow-hidden">
            {/* Map Container */}
            <div ref={containerRef} className="w-full h-full z-0 absolute inset-0" />

            {/* --- CYBER-OPS OVERLAYS --- */}
            {/* 1. Grid Overlay */}
            <div className="grid-overlay pointer-events-none z-10" />

            {/* 2. Vignette */}
            <div className="vignette pointer-events-none z-20" />

            {/* 3. Scanlines */}
            <div className="scanlines pointer-events-none z-30" />

            {/* --- LOADING & ERROR STATES --- */}
            {/* Tile Loading Indicator (Moved to avoid HUD overlap) */}
            <div className={`absolute top-40 right-4 z-[500] pointer-events-none transition-opacity duration-300 ${!tileLoadError && !mapRef.current ? 'opacity-0' : 'opacity-100'}`}>
                {tileLoadError ? (
                    <div className="bg-red-500/90 text-white text-xs px-3 py-1.5 rounded-xl shadow-lg flex items-center gap-2 backdrop-blur-md border border-red-400">
                        <span className="animate-pulse text-lg">⚠️</span>
                        <span className="font-bold">OFFLINE MODE</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                    </div>
                )}
            </div>

            {/* Critical Error Overlay (Black Screen Fix Feedback) */}
            {tileLoadError && (
                <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none bg-[#0a0f1e]/80 backdrop-blur-sm">
                    <div className="text-center p-6 rounded-2xl border border-white/10 bg-black/40">
                        <div className="text-red-500 text-4xl mb-2">📡</div>
                        <h3 className="text-white font-bold mb-1">SATELLITE LINK LOST</h3>
                        <p className="text-gray-400 text-sm">Attempting to re-establish secure grid...</p>
                    </div>
                </div>
            )}

            {/* 4. Scanning Bar Animation */}
            <div className="absolute inset-x-0 h-1 bg-[var(--neon-cyan)]/20 animate-hud-scan pointer-events-none z-40 blur-sm" />

            <style>{`
                .animate-dash { animation: dash 30s linear infinite; }
                @keyframes dash { to { stroke-dashoffset: -1000; } }
                .leaflet-div-icon { background: transparent !important; border: none !important; }
                
                /* Hide Leaflet Controls (Attribution etc) for pure immersion - add toggle later if needed or move to corner */
                .leaflet-control-container .leaflet-bottom { bottom: 80px; } /* Lift attribution above dock */
            `}</style>

            {/* 5. Radial Contact Menu */}
            {radialMenu && (
                <RadialMenu
                    x={radialMenu.x}
                    y={radialMenu.y}
                    onClose={() => setRadialMenu(null)}
                    onMessage={() => handleRadialAction('MESSAGE')}
                    onPulse={() => handleRadialAction('PULSE')}
                    onHeart={() => handleRadialAction('HEART')}
                    onProfile={() => handleRadialAction('PROFILE')}
                />
            )}
        </div>
    );
};

export default TacticalMap;
